/* eslint-disable @typescript-eslint/no-unused-vars */
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { ExternalLink } from '@/components/Link';
import TokenSymbol from '@/components/TokenLogo/TokenSymbol';
import { Tooltip } from '@/components/ToolTip';
import UnderlineToolTip from '@/components/ToolTip/UnderlineToolTip';
import { HISTORY_TYPE } from '@/constants/history';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import {
  AccountBalanceHistory,
  FundingHistory,
  LiquidityHistory,
  OrderHistory,
  TradeHistory,
  TransferHistory,
  VirtualTradeType,
} from '@/features/graph/types';
import { useChainId, useEtherscanLink } from '@/hooks/web3/useChain';
import { TokenInfo, TokenInfoFromApi } from '@/types/token';
import { formatDate } from '@/utils/timeUtils';
import { ColumnsType } from 'antd/es/table';
import { t } from 'i18next';
import _, { upperFirst } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './index.less';

import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import LiquidityRemoveTooltip from '@/components/ToolTip/LiquidityRemoveTooltip';
import TradeSideSize from '@/pages/components/TradeSideSize';
import { mappingToken } from '@/utils/token';
import { Side } from '@synfutures/sdks-perp';
import IconPay from './assets/icon_label_p.svg?react';
import IconReceive from './assets/icon_label_r.svg?react';

export const portfolioTableColumns = {
  lastUpdatedTime: {
    title: 'common.status',
    key: 'status',
    width: 119,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: OrderHistory): JSX.Element | undefined => {
      const status = record.typeString;
      let statusText = t(`common.table.status.${status}`);
      if (
        status === 'filled' &&
        WrappedBigNumber.from(record.takenSize).abs().lt(WrappedBigNumber.from(record.size).abs())
      ) {
        statusText = t(`common.table.status.PARTIAL_FILLED`);
      }
      return (
        <UnderlineToolTip title={`${t(`common.table.lastUpdatedTime`)}: ${formatDate(record.placeTimestamp * 1000)}`}>
          <span>{statusText}</span>
        </UnderlineToolTip>
      );
    },
  },
  direction: {
    title: 'common.table.direction',
    key: 'direction',
    width: 80,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: OrderHistory): JSX.Element | undefined => {
      return <>{formatDate(record.placeTimestamp * 1000)}</>;
    },
  },
  type: {
    title: 'common.table.type',
    key: 'type',
    width: 80,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: TradeHistory): JSX.Element | undefined => {
      const typeDom = (
        <span>
          <>{record.typeString}</>
        </span>
      );
      return (
        <UnderlineToolTip
          title={record.typeString === VirtualTradeType.RANGE ? <LiquidityRemoveTooltip record={record} /> : undefined}>
          {typeDom}
        </UnderlineToolTip>
      );
    },
  },
  orderPrice: {
    title: 'common.table.orderPrice',
    key: 'orderPrice',
    width: 90,
    align: 'left',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: OrderHistory): JSX.Element | undefined => {
      const price = WrappedBigNumber.from(record.orderPrice);
      return <>{price.formatPriceNumberWithTooltip()}</>;
    },
  },
  price: {
    title: 'common.table.price',
    key: 'price',
    width: 80,
    align: 'left',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: TradeHistory): JSX.Element | undefined => {
      // const price = getAdjustTradePrice(record.price, record.pair?.isInverse || false);
      const price = WrappedBigNumber.from(record.price);
      return <>{price.formatPriceNumberWithTooltip()}</>;
    },
  },
  size: {
    title: 'common.size',
    key: 'size',
    width: 140,
    align: 'left',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: TradeHistory): JSX.Element | undefined => {
      const side = record.side === 'long' ? Side.LONG : Side.SHORT;
      return (
        <div className="syn-side-size-table-item">
          <TradeSideSize
            side={side}
            size={WrappedBigNumber.from(record.size).formatNumberWithTooltip({
              isShowTBMK: true,
              suffix: record?.baseToken?.symbol,
            })}
          />
        </div>
      );
    },
  },
  action: {
    title: 'common.table.action',
    key: 'action',
    width: 80,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    render: (value: any, record: FundingHistory): JSX.Element | undefined => {
      return <>{upperFirst(record.typeString)}</>;
    },
  },
};

export function useHistoryTableColumns(
  historyType: HISTORY_TYPE,
  quotes?: (TokenInfoFromApi | undefined)[],
  showPair = true,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnsType<any> {
  const { t } = useTranslation();
  const chainId = useChainId();

  const getEtherscanLink = useEtherscanLink();
  const timestamp = useMemo(
    () => ({
      title: 'common.table.time',
      key: 'time',
      width: 140,
      align: 'left',
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (value: any, record: TradeHistory): JSX.Element | undefined => {
        return (
          <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')}>
            <div className="history-time-with-icon">
              <span>{formatDate(record.timestamp * 1000)}</span>
              {/* {record?.pair?.chainId && <img src={DAPP_CHAIN_CONFIGS[record.pair?.chainId].network.icon} />} */}
            </div>
          </ExternalLink>
        );
      },
    }),
    [getEtherscanLink],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tradeValue = useMemo(
    () => ({
      title: 'common.table.tradeValue',
      key: 'tradeV',
      width: 100,
      align: 'left',
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: TradeHistory): JSX.Element | undefined => {
        // Calculate trade value from price and size since tradeValue doesn't exist in the new API
        const tradeValue = WrappedBigNumber.from(record.price || 0)
          .mul(record.size || 0)
          .abs();
        return (
          <>
            {tradeValue.formatNumberWithTooltip({
              suffix: record?.quoteToken.symbol,
            })}
          </>
        );
      },
    }),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fee = useMemo(
    () => ({
      title: 'common.table.fee',
      key: 'fee',
      width: 150,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: TradeHistory): JSX.Element | undefined => {
        let type: 'paid' | 'received' | undefined = undefined;
        let fee = WrappedBigNumber.from(record?.tradeFee || 0).add(record?.protocolFee || 0);
        if (record.typeString === VirtualTradeType.MARKET) {
          type = 'paid';
        } else if (record.typeString === VirtualTradeType.LIMIT) {
          type = 'received';
        } else if (record.typeString === VirtualTradeType.RANGE) {
          fee = WrappedBigNumber.ZERO;
        } else if (record.typeString === VirtualTradeType.TAKE_OVER) {
          fee = WrappedBigNumber.ZERO;
        } else if (record.typeString === VirtualTradeType.LIQUIDATION) {
          type = 'paid';
        }
        return (
          <EmptyDataWrap isLoading={!record?.tradeFee || fee.lte(0)}>
            <span className="syn-futures-table_card-fee">
              <Tooltip title={type && t(`common.table.${type}`)}>
                {type && (
                  <span className="syn-futures-table_card-fee-icon">
                    {type === 'paid' ? <IconPay /> : <IconReceive />}
                  </span>
                )}
              </Tooltip>
              {WrappedBigNumber.from(fee).formatNumberWithTooltip({
                suffix: record?.quoteToken.symbol,
              })}
            </span>
          </EmptyDataWrap>
        );
      },
    }),
    [t],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fundingSize = useMemo(
    () => ({
      title: 'common.table.amount',
      key: 'amount',
      width: 80,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: FundingHistory): JSX.Element | undefined => {
        return (
          <>
            {WrappedBigNumber.from(record.value).abs().formatNumberWithTooltip({
              suffix: record?.quoteToken.symbol,
            })}
          </>
        );
      },
    }),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const transferAmount = useMemo(
    () => ({
      title: 'common.table.amount',
      key: 'amount',
      width: 120,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: TransferHistory): JSX.Element | undefined => {
        return (
          <>
            {record?.quoteToken &&
              WrappedBigNumber.from(record.value).formatNumberWithTooltip({
                suffix: record?.quoteToken.symbol,
              })}
          </>
        );
      },
    }),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const direction = useMemo(
    () => ({
      title: 'common.table.direction',
      key: 'direction',
      width: 120,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: TransferHistory): JSX.Element | undefined => {
        return <> {record.typeString !== 'scatter' ? t('common.table.transferIn') : t('common.table.transferOut')}</>;
      },
    }),
    [t],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const earnPriceRange = useMemo(
    () => ({
      title: 'common.table.earn.priceRange',
      key: 'earnPriceRange',
      width: 120,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_: any, record: LiquidityHistory): JSX.Element | undefined => {
        const priceMin = WrappedBigNumber.from(record.lowerPrice);

        const priceMax = WrappedBigNumber.from(record.upperPrice);
        return (
          <>
            {priceMin.formatPriceNumberWithTooltip()} ~ {priceMax.formatPriceNumberWithTooltip()}
          </>
        );
      },
    }),
    [],
  );

  const price = useMemo(
    () => ({
      title: 'common.price',
      key: 'fairPrice',
      width: 100,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_: any, record: LiquidityHistory): JSX.Element | undefined => {
        const fairPrice = WrappedBigNumber.from(record.fairPrice);
        return <>{fairPrice.formatPriceNumberWithTooltip()}</>;
      },
    }),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const earnAmount = useMemo(
    () => ({
      title: 'common.table.amount',
      key: 'amount',
      width: 80,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: LiquidityHistory): JSX.Element | undefined => {
        return <>{WrappedBigNumber.from(record.amount).formatNumberWithTooltip()}</>;
      },
    }),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const earnFeeEarning = useMemo(
    () => ({
      title: 'common.table.earn.feeEarning',
      key: 'feeEarning',
      width: 100,
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      render: (_value: any, record: LiquidityHistory): JSX.Element | undefined => {
        return (
          <EmptyDataWrap isLoading={!record.feeEarned || WrappedBigNumber.from(record.feeEarned).isZero()}>
            {record.feeEarned &&
              WrappedBigNumber.from(record.feeEarned).formatNumberWithTooltip({
                suffix: record?.quoteToken.symbol,
              })}
          </EmptyDataWrap>
        );
      },
    }),
    [],
  );

  // const filters = useMemo(
  //   () =>
  //     pairs?.map((pair) => ({
  //       text: pair ? (
  //         <TokenPair
  //           baseToken={pair?.rootInstrument.baseToken}
  //           quoteToken={pair?.rootInstrument.quoteToken}
  //           isShowLogo={false}
  //           instrumentAddr={pair?.rootInstrument.instrumentAddress}
  //           isInverse={pair?.rootInstrument?.isInverse}
  //           isShowExpiryTag={false}
  //           expiry={pair?.expiry}
  //         />
  //       ) : (
  //         ''
  //       ),
  //       value: pair?.id,
  //     })),
  //   [pairs],
  // );

  const pair = useMemo(
    () => ({
      title: 'common.market.tradeP',
      dataIndex: 'name',
      width: 260,
      // filters,
      // filterIcon: <IconFilter />,
      // onFilter: (value: string, record: ITradeHistory | OrderHistory) => record?.pair?.id === value,
      // filterDropdown: (props: FilterDropdownProps) => (props.filters ? <HistoryFilterDropDown {...props} /> : <></>),
      render: (text: string, record: TradeHistory): JSX.Element | undefined => {
        if (!chainId) return undefined;
        return (
          <TradeSideSize
            side={record.side ? (record.side === 'long' ? Side.LONG : Side.SHORT) : Side.FLAT}
            showBar={true}
            size={
              <TokenPairSimple
                baseToken={mappingToken({
                  token: record.baseToken,
                  chainId: chainId,
                })}
                quoteToken={mappingToken({
                  token: record.quoteToken,
                  chainId: chainId,
                })}
                // isInverse={pair?.rootInstrument?.isInverse}
              />
            }
          />
        );
      },
    }),
    [chainId],
  );

  const quoteFilters = useMemo(
    () =>
      quotes?.map((quote) => ({
        text: quote ? quote.symbol : '',
        value: quote?.address,
      })),
    [quotes],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accountType = useMemo(
    () => ({
      title: 'common.table.account',
      dataIndex: 'quote',
      className: 'history-account-type',
      width: 180,
      // filters: quoteFilters,
      // align: 'left',
      // filterIcon: <IconFilter />,
      // onFilter: (value: string, record: AccountBalanceHistory) => record?.tokenInfo.address === value,
      // filterDropdown: (props: FilterDropdownProps) => (props.filters ? <HistoryFilterDropDown {...props} /> : <></>),
      render: (_value: TokenInfo, record: AccountBalanceHistory): JSX.Element | undefined => {
        if (!chainId) return undefined;
        return (
          <>
            <TokenSymbol
              classNames="history-token-symbol"
              tokenInfo={mappingToken({
                token: record.tokenInfo,
                chainId: chainId,
              })}></TokenSymbol>
          </>
        );
      },
    }),
    [],
  );

  const columnsMap = useMemo(
    () => ({
      [HISTORY_TYPE.ORDERS]: [
        {
          ...timestamp,
          render: (value: unknown, record: OrderHistory): JSX.Element | undefined => {
            return (
              <ExternalLink href={getEtherscanLink(record.placeTxHash, 'transaction')}>
                <div className="history-time-with-icon">
                  <span>{formatDate(record.placeTimestamp * 1000)}</span>
                  {/* {record?.pair?.chainId && <img src={DAPP_CHAIN_CONFIGS[record.pair?.chainId].network.icon} />} */}
                </div>
              </ExternalLink>
            );
          },
        },
        showPair ? { ...pair, title: 'common.table.pair', width: 190, align: 'left' } : undefined,
        { ...portfolioTableColumns.size, width: 120 },
        {
          title: 'common.taken',
          key: 'FILLED_AMOUNT',
          width: 135,
          align: 'left',
          // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
          render: (value: any, record: OrderHistory): JSX.Element | undefined => {
            return <>{WrappedBigNumber.from(record.takenSize).abs().formatNumberWithTooltip({ isShowTBMK: true })}</>;
          },
        },

        {
          ...portfolioTableColumns.orderPrice,
          // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
          render: (value: any, record: OrderHistory): JSX.Element | undefined => {
            const price = WrappedBigNumber.from(record.orderPrice); // getAdjustTradePrice(oPrice, record.pair?.isInverse || false);
            return <>{price.formatPriceNumberWithTooltip()}</>;
          },
        },
        {
          ...tradeValue,
          width: 110,
          // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
          render: (value: any, record: OrderHistory): JSX.Element | undefined => {
            // Calculate trade value from orderPrice and size since tradeValue doesn't exist in the new API
            const tradeValue = WrappedBigNumber.from(record.balance || 0).abs();
            return (
              <>
                {tradeValue.formatNumberWithTooltip({
                  suffix: record?.quoteToken.symbol,
                })}
              </>
            );
          },
        },
        {
          ...fee,
          title: 'common.table.feeEarned',
          width: 150,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (_value: any, record: OrderHistory): JSX.Element | undefined => {
            const fee = WrappedBigNumber.from(record.feeRebate || 0);
            return (
              <EmptyDataWrap isLoading={!record.feeRebate}>
                <span className={fee.gt(0) ? 'highlight' : ''}>
                  {fee.formatNumberWithTooltip({
                    suffix: record?.quoteToken.symbol,
                  })}
                </span>
              </EmptyDataWrap>
            );
          },
        },
        {
          ...portfolioTableColumns.lastUpdatedTime,
          width: 89,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (value: any, record: OrderHistory): JSX.Element | undefined => {
            // Map order type to status: 1=FILLED, 2=CANCELLED, 3=CANCELLED
            const orderType = record.typeString;
            let statusText = '';
            let txHash = '';
            let timestamp = 0;

            if (orderType === 'filled') {
              // Check if partially filled
              const takenSize = WrappedBigNumber.from(record.takenSize);
              const totalSize = WrappedBigNumber.from(record.size);
              if (!takenSize.eq(totalSize)) {
                statusText = t(`common.table.status.PARTIAL_FILLED`);
              } else {
                statusText = t(`common.table.status.FILLED`);
              }
              txHash = record.fillTxHash;
              timestamp = record.fillTimestamp;
            } else if (orderType === 'cancelled') {
              statusText = t(`common.table.status.CANCELLED`);
              txHash = record.cancelTxHash;
              timestamp = record.cancelTimestamp;
            }

            return (
              <UnderlineToolTip
                overlayStyle={{ maxWidth: 300 }}
                title={
                  <ExternalLink className="tooltip" width={300} href={getEtherscanLink(txHash || '', 'transaction')}>
                    {t(`common.table.lastUpdatedTime`)}: {formatDate(timestamp * 1000)}
                  </ExternalLink>
                }>
                <span>{statusText}</span>
              </UnderlineToolTip>
            );
          },
        },
      ],
      [HISTORY_TYPE.TRADE]: [
        timestamp,
        showPair ? { ...pair, title: 'common.table.pair', width: 180, align: 'left' } : undefined,
        { ...portfolioTableColumns.type, align: 'left', width: 100 },
        { ...portfolioTableColumns.size, width: 100 },
        { ...portfolioTableColumns.price, width: 100 },
        { ...tradeValue, width: 110 },
        { ...fee, width: 120 },
      ],
      [HISTORY_TYPE.FUNDING]: [
        {
          ...timestamp,
          render: (value: unknown, record: TradeHistory): JSX.Element | undefined => (
            <>
              <div className="history-time-with-icon">
                <span>{formatDate(record.timestamp * 1000)}</span>
              </div>
            </>
          ),
        },
        showPair ? { ...pair, title: 'common.table.pair', width: 120, align: 'left' } : undefined,
        portfolioTableColumns.action,
        fundingSize,
      ],
      [HISTORY_TYPE.TRANSFERS]: [
        { ...timestamp, width: 190 },
        showPair ? { ...pair, title: 'common.table.pair', width: 120, align: 'left' } : undefined,
        direction,
        { ...transferAmount, width: 180 },
      ],
      [HISTORY_TYPE.LIQUIDITY]: [
        { ...timestamp, width: 120 },
        showPair ? { ...pair, title: 'common.table.pair', width: 120, align: 'left' } : undefined,
        {
          ...portfolioTableColumns.type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (_value: any, record: LiquidityHistory): JSX.Element | undefined => {
            return (
              <>
                {record.typeString === 'Remove'
                  ? t(`common.historyC.liqHistory.Removed`)
                  : t(`common.historyC.liqHistory.${record.typeString}`)}
              </>
            );
          },
        },
        earnPriceRange,
        price,
        earnAmount,
        earnFeeEarning,
      ],
      [HISTORY_TYPE.LIQUIDITY_MOBILE]: [
        {
          ...timestamp,
          width: 90,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (value: any, record: TradeHistory): JSX.Element | undefined => {
            return (
              <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')}>
                <div className="history-time-with-icon">
                  <span>{formatDate(record.timestamp * 1000, 'MM-DD HH:mm')}</span>
                  {/* {record?.pair?.chainId && <img src={DAPP_CHAIN_CONFIGS[record.pair?.chainId].network.icon} />} */}
                </div>
              </ExternalLink>
            );
          },
        },
        {
          ...portfolioTableColumns.type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (_value: any, record: LiquidityHistory): JSX.Element | undefined => {
            return (
              <>
                {record.typeString === 'Remove'
                  ? t(`common.historyC.liqHistory.Removed`)
                  : t(`common.historyC.liqHistory.${record.typeString}`)}
              </>
            );
          },
          width: 45,
        },
        { ...earnPriceRange, width: 115 },
        { ...earnAmount, width: 55 },
      ],
      [HISTORY_TYPE.ACCOUNT]: [
        {
          ...timestamp,
          width: 190,
          render: (value: unknown, record: AccountBalanceHistory): JSX.Element | undefined => {
            return (
              <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')}>
                <div className="history-time-with-icon">
                  <span>{formatDate((record?.timestamp || 0) * 1000)}</span>
                  {/* {record?.chainId && <img src={DAPP_CHAIN_CONFIGS[record?.chainId].network.icon} />} */}
                </div>
              </ExternalLink>
            );
          },
        },
        accountType,
        {
          ...portfolioTableColumns.type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (_value: any, record: AccountBalanceHistory): JSX.Element | undefined => {
            return <>{record.typeString === 'deposit' ? t('common.deposit') : t('common.withdraw')}</>;
          },
        },
        {
          ...transferAmount,
          width: 180, // eslint-disable-next-line @typescript-eslint/no-explicit-any
          render: (_value: any, record: AccountBalanceHistory): JSX.Element | undefined => {
            try {
              return (
                <>
                  {WrappedBigNumber.from(record.value).formatNumberWithTooltip({
                    suffix: record?.tokenInfo?.symbol,
                  })}
                </>
              );
            } catch (error) {
              console.log('🚀 ~ error:', { error, record });
            }
          },
        },
      ],
    }),
    [
      timestamp,
      showPair,
      pair,
      tradeValue,
      fee,
      fundingSize,
      direction,
      transferAmount,
      earnPriceRange,
      price,
      earnAmount,
      earnFeeEarning,
      accountType,
      getEtherscanLink,
      t,
    ],
  );
  const columnDefs = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () =>
      _.filter(
        _.get(columnsMap, [historyType]),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (c: any) => !!c,
      )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((c: { title: any }) => ({ ...c, title: t(c.title) })),
    [columnsMap, historyType, t],
  );
  return columnDefs;
}
